/**
 * Pure UI helpers for maintenance views
 * NO storage, NO VesselContext, NO event emissions
 * These are UI-only formatting and filtering functions
 */

import { CheckCircle, AlertTriangle, Clock } from 'lucide-react';

// Normalize string for comparison
export const norm = v => (v ?? '').toString().trim().toLowerCase();

// Format date for display
export const formatDate = (date) => {
  if (!date) return 'Never';
  try {
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return '—';
  }
};

// Get status display configuration
export const getStatusDisplay = (job) => {
  if (job.status === 'overdue') {
    return {
      color: 'bg-red-100 text-red-700 border-red-200',
      icon: AlertTriangle,
      text: `${Math.abs(job.daysUntilDue)} days overdue`
    };
  }
  if (job.status === 'due_soon') {
    return {
      color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      icon: Clock,
      text: `Due in ${job.daysUntilDue} days`
    };
  }
  return {
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: CheckCircle,
    text: 'Compliant'
  };
};

// Filter job history with normalized comparison
// FT-190 FIX: STRICT jobId matching ONLY - no equipmentType fallback
// Phantom history bug was caused by equipmentType matching old records to new jobs
export const filterJobHistory = (history, equipmentType, jobId = null) => {
  // FT-190: ALWAYS filter by jobId - this is the ONLY safe way to match history
  // If no jobId provided, return empty array (no legacy fallback)
  if (!jobId) {
    console.warn('[filterJobHistory] FT-190: No jobId provided, returning empty history');
    return [];
  }
  return (Array.isArray(history) ? history : []).filter(h => h.jobId === jobId);
};

// Get default image based on equipment type
export const getDefaultImage = (job) => {
  // Use equipmentType from job object, or fall back to job item name
  const type = (job?.equipmentType || job?.item || '').toLowerCase();
  const category = job?.category?.toLowerCase() || '';
  
  // Check specific equipment types - ORDER MATTERS!
  // Fire extinguisher first
  if (type.includes('fire extinguisher') || type.includes('portable fire')) {
    return '/images/maintenance/specific/fire-extinguisher.jpg';
  }
  // Distinguish between inflatable and foam life jackets
  if (type.includes('inflatable') && (type.includes('life jacket') || type.includes('lifejacket'))) {
    return '/images/maintenance/specific/inflatable-lifejacket.jpg';
  }
  if (type.includes('foam') && (type.includes('life jacket') || type.includes('lifejacket'))) {
    return '/images/maintenance/specific/foam-lifejacket.jpg';
  }
  // Default life jacket (if not specified as foam)
  if (type.includes('life jacket') || type.includes('lifejacket')) {
    return '/images/maintenance/specific/inflatable-lifejacket.jpg';
  }
  // Liferaft (must come after lifejacket to avoid false matches)
  if (type.includes('liferaft') || type.includes('life raft')) {
    return '/images/maintenance/specific/liferaft.jpg';
  }
  // Lifebuoy
  if (type.includes('lifebuoy')) {
    return '/images/maintenance/specific/lifebuoy.jpg';
  }
  if (type.includes('first aid') || type.includes('medical kit')) {
    return '/images/maintenance/specific/first-aid-kit.jpg';
  }
  // EPIRB should show generic LSA pic
  if (type.includes('epirb')) {
    return '/images/maintenance/categories/lsa.jpg';
  }
  
  // Fall back to category images
  switch(category) {
    case 'lsa':
      return '/images/maintenance/categories/lsa.jpg';
    case 'ffe':
      return '/images/maintenance/categories/ffe.jpg';
    case 'radio':
      return '/images/maintenance/categories/radio.jpg';
    case 'navigation':
      return '/images/maintenance/categories/navigation.jpg';
    case 'machinery':
      return '/images/maintenance/categories/machinery.jpg';
    case 'electrical':
      return '/images/maintenance/categories/electrical.jpg';
    case 'medical':
      return '/images/maintenance/categories/medical.jpg';
    case 'surveys':
      return '/images/maintenance/categories/surveys.jpg';
    case 'special_ops':
      return '/images/maintenance/categories/special-ops.jpg';
    default:
      // Generic fallback
      return '/images/maintenance/categories/default.jpg';
  }
};